import React, { createContext, useContext, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

export interface SectionTab {
  label: string;
  to: string;
  /** Paths (prefixes) that count as this tab. Defaults to `to`. */
  match?: string[];
  /** Only `to` itself counts (a tab whose siblings live under its path). */
  exact?: boolean;
  soon?: boolean;
  count?: number | null;
}

/**
 * The header row's slot: a screen inside a SectionShell can put its own
 * controls on the title and tabs line (Reach › Calendar's counts, week and
 * Write a post), with createPortal. undefined outside a shell; null until the
 * slot has mounted.
 */
const SectionHeadSlot = createContext<HTMLElement | null | undefined>(undefined);
export const useSectionHeadSlot = () => useContext(SectionHeadSlot);

/**
 * A top-level area (Atomize, Reach): its title, its tabs and anything on the
 * right, above the screen for the current tab, which fills the rest.
 */
export function SectionShell({
  title,
  badge,
  tabs,
  right,
  children,
}: {
  title: string;
  badge?: React.ReactNode;
  tabs: SectionTab[];
  right?: React.ReactNode;
  children: React.ReactNode;
}) {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [slot, setSlot] = useState<HTMLElement | null>(null);
  const active = (t: SectionTab) =>
    t.exact ? pathname === t.to : (t.match || [t.to]).some((m) => pathname.startsWith(m));
  return (
    <div className="ink-section-shell">
      <div className="ink-section-head">
        <h1 className="ink-h1 ink-section-title">
          {title}
          {badge}
        </h1>
        {tabs.length ? (
          <div className="ink-seg">
            {tabs.map((t) => (
              <button
                key={t.label}
                type="button"
                data-active={active(t) ? 'true' : 'false'}
                disabled={t.soon}
                onClick={() => !t.soon && navigate(t.to)}
              >
                {t.label}
                {t.count ? <span className="ink-section-count">{t.count}</span> : null}
                {t.soon ? <span className="ink-soon">SOON</span> : null}
              </button>
            ))}
          </div>
        ) : null}
        <div className="ink-section-slot" ref={setSlot} />
        {right ? <div className="ink-section-right">{right}</div> : null}
      </div>
      <div className="ink-section-body">
        <SectionHeadSlot.Provider value={slot}>{children}</SectionHeadSlot.Provider>
      </div>
    </div>
  );
}

/** The card shown in place of an area this site doesn't have. */
export function SectionLocked({
  title,
  lede,
  points,
  children,
}: {
  title: string;
  lede: string;
  points: string[];
  children?: React.ReactNode;
}) {
  return (
    <div className="ink-locked">
      <div className="ink-locked-title">{title}</div>
      <p className="ink-locked-lede">{lede}</p>
      {points.length ? (
        <ul className="ink-locked-points">
          {points.map((p) => (
            <li key={p}>{p}</li>
          ))}
        </ul>
      ) : null}
      {children}
    </div>
  );
}
